import './FirePopUp.scss';
import React, { useEffect, useState, useMemo, useCallback } from "react";
import WeatherConditions from '../../WeatherConditions';
import calendar from '../../../assets/icons/calendar-icon.png';
import locationIcon from '../../../assets/icons/location-icon.png';
import left from '../../../assets/icons/left.png';
import moment from 'moment-timezone';
import LiveTime from './LiveTime';
import PieChart from './PieChart';
import RiskComponent from '../../RiskComponent';
import Comments from '../../Comments';
import optionsIcon from '../../../assets/icons/options-icon.png';
import { useFiresContext } from '../../../contexts/FiresContext';
import { useUserContext } from '../../../contexts/UserContext';
import warningIcon from '../../../assets/icons/hazard-icon.png';
import StateAlerts from './StateAlerts';
import Tweets from './Tweets';
import axios from 'axios';
import mapboxgl from 'mapbox-gl';
import { useAreasContext } from '../../../contexts/AreasContext';
import { useTeamsContext } from '../../../contexts/TeamsContext';
import Dispatch from './Dispatch';
import Spinner from '../../Spinner';
// Dynamic flag icon loading - no need to manually import each flag
import FireSimulation from './FireSpreadSimulation/FireSimulation';
import AIPrescribedFirePrediction from './AIPrescribedFirePrediction';
import StreetView from './StreetView';
import * as turf from '@turf/turf';
import TerrainPopup from './TerrainPopUp';
import { getTerrainBounds } from './terrainUtils';
import NearbyShelters from './NearbyShelters';
import fireContainedUrl from '../../../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../../../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../../../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../../../assets/icons/fire-almost-gray.png';
import { WarningCircle, CaretLeft, CaretRight, Sparkle } from '@phosphor-icons/react';


export default function FirePopup({ panel, setPanel, user, setUser, fire, map }) {
    const { is3D, setIs3D, getToken } = useUserContext();
    const { teamAreas, myAreas } = useAreasContext();
    const { teams } = useTeamsContext();
    const { setModal, updateFireGeometry, plotFires, fires } = useFiresContext();

    const userSub = user?.accessToken.payload.sub;
    
    const [tab, setTab] = useState('Overview');
    const [geom, setGeom] = useState(null);
    const [stateAlerts, setStateAlerts] = useState(null);
    const [weatherData, setWeatherData] = useState(null);
    const [dispatchPerimeter, setDispatchPerimeter] = useState(null);
    const [isLoadingGeometry, setIsLoadingGeometry] = useState(fire?.fire_id ? true : false);
    const [nearbyShelters, setNearbyShelters] = useState(null);
    const [terrainData, setTerrainData] = useState(null);
    const [location, setLocation] = useState(null);
    const [country, setCountry] = useState(null);
    const [flagIcon, setFlagIcon] = useState(null);
    const [streetViewImages, setStreetViewImages] = useState(null);
    const [isLoadingStreetView, setIsLoadingStreetView] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    const hasDispatch = useMemo(() => teams?.some(t => t.id === 2), [teams]);

    const isCanadian = useMemo(
        () => /CWFIS|CIFFC/.test(fire?.data_source ?? ''),
        [fire?.data_source]
    );


    // Memoized fire icon selection
    const returnFireIcon = useCallback((perContained) => {
        const pc = perContained ?? 0;
      
        if (pc < 41)          return fireCircleUrl;            // 0–40
        if (pc < 91)          return fireHalfContainedUrl;     // 41–90
        if (pc < 100)         return fireAlmostGray;           // 91–99
        /* pc === 100 */      return fireContainedUrl;         // 100
    }, []);    

    // Memoized WKT parsing function
    const WKTtoRings = useCallback((wkt) => {
        const match = wkt.match(/^MULTIPOLYGON\s*\(\(\(\s*([\s\S]+?)\s*\)\)\)$/i);
        if (!match) {
          throw new Error('Invalid MULTIPOLYGON WKT: ' + wkt);
        }
        const coordsText = match[1];
        // Split into individual polygons
        const polygonStrings = coordsText.split(/\)\),\s*\(\(/);
        // For each polygon, split into its rings and parse coordinates
        return polygonStrings.map(polyStr => {
          const ringStrings = polyStr.split(/\)\s*,\s*\(/);
          return ringStrings.map(ringStr =>
            ringStr.trim().split(/\s*,\s*/).map(pair => {
              const [lon, lat] = pair.trim().split(/\s+/).map(Number);
              return [lon, lat];
            })
          );
        });
    }, []);
      
    const tabs = useMemo(() => [
        'Overview', ...(hasDispatch ? ['Dispatch'] : []),
        ...(!isCanadian      ? ['State Alerts'] : []),
        'Street View',
        'Risk',
        'Twitter',
        'Comments',
        'AI Fire Spread Simulation',
        'Nearby Shelters',
        'Interactive 3D Terrain Elevation',
        'AI Prescribed Fire Prediction',
      ], [hasDispatch, isCanadian]);

    const formatType = useCallback(type => { return { FI:'Fire Incident', FM:'Fire Monitoring', L:'Large Fire' }[type] || 'Unknown'; }, []);
    const formatDate = useCallback(ts => ts ? moment.utc(ts).local().format('YYYY-MM-DD hh:mm:ss A') : 'N/A', []);

    // Location fetching function
    const fetchLocation = async () => {
        try {
          const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${fire.longitude},${fire.latitude}.json`;
  
          const { data } = await axios.get(url, {
            params: {
              access_token:
                'pk.eyJ1IjoiY2VzYXJtbG5keiIsImEiOiJjbHJ0b2JqZjEwOHpiMmtxa25ubzZ0ZXE3In0.18DfsEFZiecc6H8jHdMOIQ'
            }
          });
  
          const context = data.features?.[0]?.context || [];
          let city = '';
          let state = '';
          let countryCode = '';
  
          context.forEach((c) => {
            if (c.id.includes('place')) {
              city = c.text;
            } else if (c.id.includes('region')) {
              state = c.text;
            } else if (c.id.includes('country')) {
              countryCode = c.short_code?.toLowerCase();
            }
          });
  
          setLocation(city && state ? `${city}, ${state}` : '');
          setCountry(countryCode);
          
          // Load flag icon dynamically if country code is available
          if (countryCode) {
            loadFlagIcon(countryCode);
          }
        } catch (err) {
          console.error('Mapbox geocoding failed:', err);
          setLocation('');
          setCountry(null);
          setFlagIcon(null);
        }
    };

    // Street view images fetching function
    const fetchStreetViewImages = async () => {
        try {
            setIsLoadingStreetView(true);
            const token = await getToken();
            const response = await axios.get(
                `https://firewatch-api.space-eyes.com/google-street-images?latitude=${fire.latitude}&longitude=${fire.longitude}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (response.data && response.data.images) {
                setStreetViewImages(response.data.images);
            }
        } catch (error) {
            console.error('Error fetching street view images:', error);
        } finally {
            setIsLoadingStreetView(false);
        }
    };

    // Street view navigation functions
    const goToPreviousImage = () => {
        if (streetViewImages && streetViewImages.length > 0) {
            setCurrentImageIndex((prevIndex) => 
                prevIndex === 0 ? streetViewImages.length - 1 : prevIndex - 1
            );
        }
    };

    const goToNextImage = () => {
        if (streetViewImages && streetViewImages.length > 0) {
            setCurrentImageIndex((prevIndex) => 
                prevIndex === streetViewImages.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    // Dynamic flag loading function
    const loadFlagIcon = async (countryCode) => {
        if (!countryCode) return;
        
        try {
            // Dynamically import the flag icon based on country code
            const flagModule = await import(`../../../assets/icons/flag-icons-main/flags/4x3/${countryCode}.svg`);
            setFlagIcon(flagModule.default);
        } catch (error) {
            console.log(`Flag icon for ${countryCode} not found, using fallback`);
            setFlagIcon(null);
        }
    };

    // Memoized flag selection (keeping for backward compatibility with data source)
    const returnFlag = useCallback((country) => {
        switch (country) {
            case 'United States': return null // Will use dynamic flag instead
            case 'Canada': return null // Will use dynamic flag instead
        }
    }, []);

    // Memoized WKT to GeoJSON conversion
    const wktToGeoJSON = useCallback(wkt => {
        const match = wkt.match(/^MULTIPOLYGON\s*\(\(\(\s*([\s\S]+?)\s*\)\)\)$/i);
        if (!match) throw new Error('Invalid WKT MULTIPOLYGON: ' + wkt);
        const coordsText = match[1];
        const polygons = coordsText.split(/\)\),\(\(/).map(polyText => {
          const rings = polyText.split(/\)\s*,\s*\(/).map(ringText =>
            ringText.trim().split(/\s*,\s*/).map(pair => {
              const [x,y] = pair.trim().split(/\s+/).map(Number);
              return [x, y];
            })
          );
          return rings;
        });
        return { type: 'MultiPolygon', coordinates: polygons };
    }, []);
    
    // Memoized centroid calculation
    const getCentroid = useCallback(geojson => turf.centroid(geojson).geometry.coordinates, []);
    
    // Memoized 3D elevation plotting
    const plot3DElevation = useCallback((map, fire) => {
        let location = geom
          ? getCentroid(wktToGeoJSON(geom))
          : [fire.longitude, fire.latitude];
    
        const bounds = getTerrainBounds(location[1], location[0]);
        const latKm = (bounds.north - bounds.south) * 111.32;
        const lonKm = (bounds.east - bounds.west) * 111.32 * Math.cos(location[1] * Math.PI/180);
        const areaLengthKm = latKm.toFixed(1);
        const areaWidthKm  = lonKm.toFixed(1);
        const areaSizeKm2  = (latKm * lonKm).toFixed(1);
       
        setTerrainData({
            lat:location[1],
            lon:location[0],
            areaLengthKm:areaLengthKm,
            areaWidthKm:areaWidthKm,
            areaSizeKm2:areaSizeKm2,
        });
    }, [geom, getCentroid, wktToGeoJSON]);
    
    // Memoized radius plotting
    const plotRadius = useCallback((map, fire, radius) => {
        // clean up layers & source
        map.current.getStyle().layers
          .filter(l => l.id.includes(`Layer-fire-${radius}mi`))
          .forEach(l => map.current.removeLayer(l.id));
        if (map.current.getSource(`Source-fire-${radius}mi`))
          map.current.removeSource(`Source-fire-${radius}mi`);
    
        // Build geometry
        const featureGeometry = geom
          ? turf.buffer(wktToGeoJSON(geom), radius, { units: 'miles' }).geometry
          : turf.circle([fire.longitude, fire.latitude], radius, { units: 'miles', steps: 64 }).geometry;
    
        const geojson = {
          type: 'FeatureCollection',
          features: [{ type: 'Feature', properties: fire, geometry: featureGeometry }]
        };
    
        map.current.addSource(`Source-fire-${radius}mi`, { type: 'geojson', data: geojson });
        map.current.addLayer({
          id: `Layer-fire-${radius}mi-fill`,
          type: 'fill',
          source: `Source-fire-${radius}mi`,
          paint: {
            'fill-color': radius === 5 ? 'rgba(206,206,206,0.47)' : 'rgba(255,87,41,0.47)'
          }
        });
        map.current.addLayer({
          id: `Layer-fire-${radius}mi-outline`,
          type: 'line',
          source: `Source-fire-${radius}mi`,
          paint: {
            'line-color': radius === 5 ? 'rgba(211,211,211,0.84)' : 'rgba(211,211,211,0.44)',
            'line-width': 2
          }
        });
    }, [geom, wktToGeoJSON]);
      
    // Memoized arrow rotation calculation
    const calculateArrowRotation = useCallback((windFromDeg) => {
        const blowingToDeg = (windFromDeg + 180) % 360; 
        return blowingToDeg; 
    }, []); 

    // Memoized cone coordinate generation
    const generateConeCoordinates = useCallback(([lng0, lat0], lengthMiles, widthMiles, rotationDeg, points = 64) => {
        const coords = [];
        const latRad = (lat0 * Math.PI) / 180;
        const rotRad = (rotationDeg * Math.PI) / 180;
    
        const tipLat = lat0 + (lengthMiles / 69) * Math.cos(rotRad);
        const tipLng = lng0 +
          (lengthMiles / (69 * Math.cos(latRad))) *
          Math.sin(rotRad);
        coords.push([tipLng, tipLat]);
    
        const startDeg = rotationDeg + 90;
        const endDeg = rotationDeg + 270;
        const step = (endDeg - startDeg) / points;
        for (let i = 0; i <= points; i++) {
          const angleDeg = startDeg + step * i;
          const angRad = (angleDeg * Math.PI) / 180;
          const lat = lat0 + (widthMiles / 69) * Math.cos(angRad);
          const lng = lng0 +
            (widthMiles / (69 * Math.cos(latRad))) *
            Math.sin(angRad);
          coords.push([lng, lat]);
        }
    
        coords.push(coords[0]);
        return coords;
    }, []); 

    const getFireGeometry = useCallback(() => {
        if (fire && fire.fire_id) {
            setIsLoadingGeometry(true);
            axios.get(`https://firewatch-api.space-eyes.com/fire-geoms?fire_id=${fire.fire_id}`)
            .then((response) => {
                if (response.data.geoms && response.data.geoms.length > 0) {
                    const geometry = response.data.geoms[0].geom;
                    setGeom(geometry);
                    
                    // If this fire has geom_check true, recenter it to the centroid
                    if (fire.geom_check) {
                        // Calculate centroid of the perimeter using memoized function
                        const centroid = turf.centroid({ type: 'MultiPolygon', coordinates: WKTtoRings(geometry) });
                        const centroidCoords = centroid.geometry.coordinates;
                        
                        // Update the fire's position to the centroid and replot
                        updateFireGeometry(fire.fire_id, geometry);
                        const updatedFires = fires.map(f => 
                            f.fire_id === fire.fire_id 
                                ? { ...f, geom: geometry, longitude: centroidCoords[0], latitude: centroidCoords[1] }
                                : f
                        );
                        plotFires(map, updatedFires);
                        
                        // Update the fire object for wind cone plotting
                        const updatedFire = { ...fire, longitude: centroidCoords[0], latitude: centroidCoords[1] };
                        // Call getWeatherData with updated coordinates
                        getWeatherDataWithUpdatedFire(updatedFire);
                    } else {
                        // If no geom_check, just call getWeatherData with original fire
                        getWeatherData();
                    }
                } else {
                    // If no geometry, call getWeatherData with original fire
                    getWeatherData();
                }
                setIsLoadingGeometry(false);
            }).catch((err) => {
                console.log('Error fetching fire geometry:', err);
                setIsLoadingGeometry(false);
            });
        } else {
            // If no fire_id, we don't need to load geometry
            setIsLoadingGeometry(false);
            // Still call getWeatherData for fires without fire_id
            getWeatherData();
        }
    }, [fire, WKTtoRings, updateFireGeometry, plotFires, fires, map]);

    const getWeatherData = useCallback(() => {
        getWeatherDataWithUpdatedFire(fire);
    }, [fire]);

    const getWeatherDataWithUpdatedFire = useCallback((fireToUse) => {
        axios.get(`https://firewatch-api.space-eyes.com/weather/current?lat=${fireToUse.latitude}&long=${fireToUse.longitude}`)
        .then((response) => {
            setWeatherData(response.data);

            if (response.data.wind && response.data.wind.deg) {
                const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`Layer-fire-wind-cone`));

                if (layers.length > 0) {
                    layers.forEach((layer) => map.current.removeLayer(layer.id))
                }

                if (map.current.getStyle().sources[`Source-fire-wind-cone`]) {
                    map.current.removeSource(`Source-fire-wind-cone`);
                }

                // Use the fire coordinates (which may be updated centroid coordinates)
                let location = [fireToUse.longitude, fireToUse.latitude];

                const coneCoordinates = generateConeCoordinates(location, 2, 0.6, calculateArrowRotation(response.data.wind.deg));
    
                const geometry = {
                    type: 'Feature',
                    properties: fireToUse,
                    geometry: {
                        type: 'Polygon',
                        coordinates: [coneCoordinates],
                    }
                }
                
                map.current.addSource(`Source-fire-wind-cone`, {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [geometry],
                    },
                });
                    
                map.current.addLayer({
                    'id': `Layer-fire-wind-cone-${fireToUse.fire_id}-fill`,
                    'type': 'fill',
                    'source': `Source-fire-wind-cone`,
                    'layout': {},
                    'paint': {
                    'fill-color': 'rgba(255, 229, 34, 0.47)',
                    }
                });

                map.current.addLayer({
                    'id': `Layer-fire-wind-cone-${fireToUse.fire_id}-outline`,
                    'type': 'line',
                    'source': `Source-fire-wind-cone`,
                    'layout': {},
                    'paint': {
                        'line-color': 'rgba(105, 105, 105, 0.84)',
                        'line-width': 1
                    }
                });
            }

            if (response.data.wind && response.data.wind.speed && (response.data.wind.speed * 2.237) >= 50 ) {
                const location = [fire.longitude, fire.latitude];

                const existingPopups = document.getElementsByClassName('popup');
                Array.from(existingPopups).forEach((popup) => popup.remove());

                new mapboxgl.Popup({ offset: 25, maxWidth: '600px', className: 'popup' })
                .setLngLat(location)
                .setHTML(`
                <div>
                    <div class='alert-popup-title'>High Wind Warning)</div>
                    <div class='alert-popup-row'>
                        <div class='alert-popup-row-name'>Wind Speed:</div>
                        <div class='alert-popup-row-value'>${(response.data.wind.speed * 2.237).toFixed(2)} mph</div>
                    </div>
                </div>
                `)
                .addTo(map.current);
                    }
                }).catch((err) => {
                    console.log(err);
                });
    }, [map, generateConeCoordinates, calculateArrowRotation]);

    // Memoized fire icon for this fire
    const fireIcon = useMemo(() => returnFireIcon(fire?.perContained), [returnFireIcon, fire?.perContained]);

    // Memoized fire location
    const fireLocation = useMemo(() => {
        if (geom) {
            try {
                return getCentroid(wktToGeoJSON(geom));
            } catch (e) {
                return [fire?.longitude, fire?.latitude];
            }
        }
        return [fire?.longitude, fire?.latitude];
    }, [geom, fire?.longitude, fire?.latitude, getCentroid, wktToGeoJSON]);

    // Memoized uploader text
    const uploaderText = useMemo(() => {
        if (!fire) return 'Not Available';
        const { fire_id, cognito_user_id, data_source } = fire;
        const userSub = user?.accessToken.payload.sub;
        if (!fire_id.toUpperCase().includes('USER-')) return `Source: ${data_source?.replaceAll('(x-weather)', '') || 'Not Available'}`;
        return cognito_user_id === userSub ? 'Uploaded By You' : 'Uploaded By Community User';
    }, [fire?.fire_id, fire?.cognito_user_id, fire?.data_source, user?.accessToken.payload.sub]);

    useEffect(() => {
        if (tab !== 'Overview') setTab('Overview');

        if (map.current && fire && !isLoadingGeometry) {
            // Only plot radius circles if the fire doesn't have a perimeter (geom_check is false)
            if (!fire.geom_check) {
                plotRadius(map, fire, 5);
                if (!geom) plotRadius(map, fire, 1);
            }
            
            const perimLayers = map.current.getStyle().layers?.filter(l =>
                l.id.includes('Layer-Perimeter')
              ) || [];

            perimLayers.forEach(l => {
                if (map.current.getLayer(l.id)) {
                    map.current.removeLayer(l.id);
                }
            });

            if (map.current.getSource('Source-Perimeter')) {
                map.current.removeSource('Source-Perimeter');
            }

              try {
                if (geom) {
                    const rings = WKTtoRings(geom);   
                    const perimeterFeature = turf.multiPolygon(rings, fire);
                  
                    map.current.addSource('Source-Perimeter', {
                      type: 'geojson',
                      data: turf.featureCollection([perimeterFeature]),
                      maxzoom: 22
                    });
                  
                    // 5️⃣ Add fill layer
                    map.current.addLayer({
                      id: 'Layer-Perimeter-fill',
                      type: 'fill',
                      source: 'Source-Perimeter',
                      paint: {
                        'fill-color': '#FFAB91',
                        'fill-opacity': 0.5
                      }
                    });
                  
                    // 6️⃣ Add outline layer
                    map.current.addLayer({
                      id: 'Layer-Perimeter-outline',
                      type: 'line',
                      source: 'Source-Perimeter',
                      paint: {
                        'line-color': '#E64A19',
                        'line-width': 2
                      }
                    });
                }

              } catch (exception) {
                console.log('error creating multipolygon');
              }
            const center = geom
              ? getCentroid(wktToGeoJSON(geom))
              : [fire.longitude, fire.latitude];
            if (panel.name === 'Fire PopUp') {
              map.current.flyTo({ center, pitch: 0, offset: [175, 0], zoom: 11 });
            }
    
            if (is3D) {
                setIs3D(false);
            }
        }

        return () => {
            const layers = map.current.getStyle().layers.filter((layer) => layer.id.includes(`fire`));

            if (layers.length > 0) {
                layers.forEach((layer) => map.current.removeLayer(layer.id))
            }
    
            if (map.current.getStyle().sources[`Source-fire-5mi`]) {
                map.current.removeSource(`Source-fire-5mi`);
            }

            const perimLayers = map.current.getStyle().layers?.filter(l =>
                l.id.includes('Layer-Perimeter')
              ) || [];
              perimLayers.forEach(l => {
                if (map.current.getLayer(l.id)) {
                  map.current.removeLayer(l.id);
                }
              });
              if (map.current.getSource('Source-Perimeter')) {
                map.current.removeSource('Source-Perimeter');
            }

            map.current.getStyle().layers
                .filter(l => l.id.startsWith('Layer-Shelter-'))
                .forEach(l => map.current.removeLayer(l.id));

            ['Source-Shelters','Source-Shelter-Routes'].forEach(src => {
            if (map.current.getSource(src)) map.current.removeSource(src);
            });

            if (map.current.hasImage('shelter-icon')) {
            map.current.removeImage('shelter-icon');
            }
        }
    }, [fire, geom, isLoadingGeometry]);

    // Call getFireGeometry when component mounts
    useEffect(() => {
        if (fire) {
            getFireGeometry();
        }
    }, [fire]);

    useEffect(() => {
        if (fire && tab === 'Interactive 3D Terrain Elevation') {
            plot3DElevation(map, fire);
        }
    }, [fire, tab])

    // Fetch location, flag, and street view images when fire changes
    useEffect(() => {
        // Reset all location-related state when fire changes
        setLocation(null);
        setCountry(null);
        setFlagIcon(null);
        setStreetViewImages(null);
        setIsLoadingStreetView(false);
        setCurrentImageIndex(0);
        if (fire?.fire_id) {
            fetchLocation();
            fetchStreetViewImages();
        }
    }, [fire?.fire_id]);

    const getUploaderText = useCallback((fire) => {
        const { fire_id, cognito_user_id, data_source } = fire;
        const userSub = user?.accessToken.payload.sub;
        if (!fire_id.toUpperCase().includes('USER-')) return `Source: ${data_source?.replaceAll('(x-weather)', '') || 'Not Available'}`;
        return cognito_user_id === userSub ? 'Uploaded By You' : 'Uploaded By Community User';
    }, [fire, user]);

    const timeDifference = (startTimestamp, endTimestamp) => {
        const start = new Date(startTimestamp);
        const end = new Date(endTimestamp);

        const diffMs = Math.abs(end - start); // Difference in milliseconds

        const diffSeconds = Math.floor(diffMs / 1000) % 60;
        const diffMinutes = Math.floor(diffMs / (1000 * 60)) % 60;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const parts = [];
        if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
        if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
        if (diffMinutes > 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
        if (diffSeconds > 0) parts.push(`${diffSeconds} second${diffSeconds > 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : '0 seconds';
    }

    const fireContainmentChange = (oldPercentage, newPercentage) => {
        const difference = newPercentage - oldPercentage;
      
        const changeType = difference > 0 ? "increased" : "decreased";
        const absoluteDifference = Math.abs(difference);
      
        return `${changeType} by ${absoluteDifference}%`;
    }

    const tabStageSwitch = (stage) => {
        switch (stage) {
            case 'Overview':
                return (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '1rem' }}>
                    {fire.description && <div className='fire-popup-details-fields-left-field' style={{ alignItems: 'flex-start' }}>
                        <div className='fire-popup-details-fields-left-field-value'>Description</div>
                        <div className='fire-popup-details-fields-left-field-title' style={{ textAlign: 'start' }}>{fire.description}</div>
                    </div>}
                    {fire.category && <div className='fire-popup-details-fields-left-field' style={{ alignItems: 'flex-start' }}>
                        <div className='fire-popup-details-fields-left-field-value'>Category</div>
                        <div className='fire-popup-details-fields-left-field-title'>{fire.category}</div>
                    </div>}
                    <div className='fire-popup-details'>
                        <div className='fire-popup-details-fields'>
                            <div className='fire-popup-details-fields-left'>
                                <div className='fire-popup-details-fields-left-field'>
                                    <div className='fire-popup-details-fields-left-field-value'>Type</div>
                                    <div className='fire-popup-details-fields-left-field-title'>{fire.type ? formatType(fire.type) : 'Not Available'}</div>
                                </div>
                                <div className='fire-popup-details-fields-left-field'>
                                    <div className='fire-popup-details-fields-left-field-value'>Fuels</div>
                                    <div className='fire-popup-details-fields-left-field-title'>{fire.fuels ? fire.fuels : 'Not Available'}</div>
                                </div>
                            </div>
                            <div className='fire-popup-details-fields-right'>
                                <div className='fire-popup-details-fields-right-field'>
                                    <div className='fire-popup-details-fields-right-field-value'>Area (Acres)</div>                               
                                    <div className='fire-popup-details-fields-right-field-title'>{fire.areaAc ? fire.areaAc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : fire.areaAC ? fire.areaAC.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 'Not Available'}</div>
                                </div>
                                <div className='fire-popup-details-fields-right-field'>
                                    <div className='fire-popup-details-fields-right-field-value'>Cause</div>
                                    <div className='fire-popup-details-fields-right-field-title'>{fire.cause ? fire.cause : 'Not available'}</div>
                                </div>
                            </div>
                        </div>
                        <WeatherConditions coordinates={[fire.latitude, fire.longitude]} parentWeatherData={weatherData}/>
                    </div>
                </div>
                )
            case 'Dispatch':
                return (
                    (!myAreas || !teamAreas) ?
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                        <div style={{ fontSize: '0.9rem', color: 'white' }}>PIF's loading in...</div>
                        <Spinner />
                    </div>
                    :
                    <Dispatch 
                        dispatchPerimeter={dispatchPerimeter}
                        setDispatchPerimeter={setDispatchPerimeter}
                        map={map}
                        myAreas={myAreas}
                        teamAreas={teamAreas}
                        fire={fire}
                        getToken={getToken}
                    />
                )
            case 'Risk':
                return (
                    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '0.5rem', marginTop: '0.3rem' }}>
                        <div style={{ fontSize: '2rem' , color: 'black' }}>Risk</div>
                        <div style={{ fontSize: '0.8rem' , color: 'black' }}>A detailed visual breakdown of wildfire risk factors at a specific location, combining multiple datasets into an intuitive interface. Metrics like Overall Risk, Damage Potential, Burnable Land, and Fireline Intensity are displayed as percentages or status indicators, offering a snapshot of exposure and vulnerability. These values are calculated using factors such as fire history, vegetation, proximity to infrastructure, suppression resources, and environmental conditions.</div>
                            <RiskComponent 
                                user={user} 
                                setUser={setUser} 
                                coordinates={[fire.longitude, fire.latitude]}
                            />
                    </div>
                )
            case 'Twitter':
                return (
                   <Tweets fire={fire}/>
                )
            case 'Comments':
                return (
                    <div>
                        <div style={{ fontSize: '2rem' , color: 'black' }}>Comments </div>
                        <div style={{ fontSize: '0.8rem' , color: 'black', marginBottom: '0.5rem' }}>Community-driven section where subscribed users can share observations, updates, and images related to a specific fire. Whether reporting conditions, asking questions, or offering support, this feature fosters real-time engagement and collaboration, giving the public a voice and strengthening situational awareness through local insights.</div>
                        <Comments 
                            user={user} 
                            setUser={setUser}
                            id={fire.fire_id}
                        />
                    </div>

                )
            case 'State Alerts':
                return (
                    <StateAlerts 
                        fire={fire}
                        stateAlerts={stateAlerts}
                        setStateAlerts={setStateAlerts}
                    />
                )
            case 'Street View':
                return (
                    <StreetView latitude={fire.latitude} longitude={fire.longitude} getToken={getToken} images={streetViewImages}/>
                )
            case 'AI Fire Spread Simulation':
                return (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <FireSimulation map={map} setIs3D={setIs3D} getToken={getToken} latitude={fire.latitude} longitude={fire.longitude} fireID={fire.fire_id} fire={fire}/>
                    </div>
                )
            case 'AI Prescribed Fire Prediction':
                return (
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <AIPrescribedFirePrediction map={map} setIs3D={setIs3D} getToken={getToken} latitude={fire.latitude} longitude={fire.longitude} fire={fire}/>
                    </div>
                )

            case 'Interactive 3D Terrain Elevation':
                return (
                    <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingBottom: '1rem' }}>
                        <div style={{ fontSize: '2rem' , color: 'black' }}>Interactive 3D Terrain Elevation</div>
                        <div style={{ fontSize: '0.8rem' , color: 'black', marginBottom: '0.5rem', marginTop: '0.3rem' }}>A dynamic, 3D-rendered model of the terrain surrounding the fire's location, offering a detailed view of elevation changes and landscape features. This tool helps users better understand how slopes, valleys, and ridges may influence the direction and speed of fire spread. Fully interactive, the elevation model enhances situational awareness by visualizing topographic risk factors in a clear and intuitive format.</div>
                        {terrainData ? 
                        <TerrainPopup 
                           lat={terrainData.lat}
                           lon={terrainData.lon}
                           areaLengthKm={terrainData.areaLengthKm}
                           areaWidthKm={terrainData.areaWidthKm}
                           areaSizeKm2={terrainData.areaSizeKm2}
                         />
                        :
                        <Spinner />}
                    </div>
                )
            case 'Nearby Shelters':
                return (
                    <NearbyShelters map={map} latitude={fire.latitude} longitude={fire.longitude} nearbyShelters={nearbyShelters} setNearbyShelters={setNearbyShelters} getToken={getToken}/>
                )
                
        }
    }

    // Early return if fire is null or panel is not Fire PopUp
    if (!fire || panel.name !== 'Fire PopUp') {
        return null;
    }

    return (
        <div style={{ display: 'flex', flex: 1, height: '100%' }}>
            {/* Loading overlay */}
            {isLoadingGeometry && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                    flexDirection: 'column',
                    gap: '1rem'
                }}>
                    <Spinner />
                    <div style={{ color: 'white', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        Loading fire data...
                    </div>
                </div>
            )}
              <aside className="fire-popup-sidebar">
                {tabs.map(t => {
                    const isAITab = t === 'AI Fire Spread Simulation' || t === 'AI Prescribed Fire Prediction';
                    return (
                    <button
                        key={t}
                        onClick={()=>setTab(t)}
                        className={[
                          'fire-popup-sidebar-tab',
                          t === 'AI Fire Spread Simulation' && 'simulation-tab',
                          t === 'AI Prescribed Fire Prediction' && 'prescribed-fire-tab',
                          t === tab && 'active'
                        ].filter(Boolean).join(' ')}
                      >
                        {isAITab && (
                          <Sparkle 
                            size={16} 
                            color={t === tab ? "#1976d2" : "#ff6b35"} 
                            weight="fill"
                            className="fire-popup-sidebar-tab-icon"
                          />
                        )}
                        {t}
                      </button>
                      
                    )
                })}
            </aside>
        <div className='fire-popup'>
        {userSub === fire.cognito_user_id &&
            <img 
                src={optionsIcon}
                className='fire-popup-options'
                onClick={() => {
                    setModal({
                        type: 'edit-and-delete',
                        content: fire
                    })
                }}
            />
            }
            <div className='fire-popup-back-btn' 
                onClick={() => {
                    setPanel({
                        name: 'Fires', 
                        content: null
                    });
                }}
            >
                <img 
                    src={left} 
                    className="fire-popup-back-btn-icon"
                />
                Back to fires
            </div>
            <div className='fire-popup-header'>
                <div className='fire-popup-header-title'>
                    <img src={fireIcon} className='fire-popup-header-title-icon'/>
                    <div className='fire-popup-header-title-text'>{fire.name ? fire.name : 'Not Available'}</div>
                </div>
                <div className='fire-popup-header-location-container' style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div className='fire-popup-header-location-container-text'>{location ? location : <Spinner />}</div>
                    {country && flagIcon && (
                        <img 
                            src={flagIcon} 
                            alt={`${country.toUpperCase()} flag`}
                            className='fire-popup-header-location-container-flag'
                        />
                    )}
                </div>
                {streetViewImages && streetViewImages.length > 0 && (
                    <div className='fire-popup-header-street-view-container'>
                        <img 
                            src={streetViewImages[currentImageIndex]} 
                            alt="Street View"
                            className='fire-popup-header-street-view'
                        />
                        {streetViewImages.length > 1 && (
                            <>
                                <button 
                                    className='fire-popup-header-street-view-nav fire-popup-header-street-view-nav-left'
                                    onClick={goToPreviousImage}
                                    aria-label="Previous image"
                                >
                                    <CaretLeft size={16} />
                                </button>
                                <button 
                                    className='fire-popup-header-street-view-nav fire-popup-header-street-view-nav-right'
                                    onClick={goToNextImage}
                                    aria-label="Next image"
                                >
                                    <CaretRight size={16} />
                                </button>
                                <div className='fire-popup-header-street-view-indicator'>
                                    {currentImageIndex + 1} / {streetViewImages.length}
                                </div>
                            </>
                        )}
                    </div>
                )}
                {isLoadingStreetView && (
                    <div className='fire-popup-header-street-view-loading'>
                        <div className='fire-popup-header-street-view-skeleton'></div>
                    </div>
                )}
            </div>
            <div className="fire-popup-main">
                <div className="fire-popup-main-left">
                    <div className="fire-popup-main-left-subheader">
                        <div className="fire-popup-main-left-subheader-status">
                            <div className="fire-popup-main-left-subheader-status-title">Status</div>
                            <div
                                className="fire-popup-main-left-subheader-status-value"
                                style={{
                                    color: fire.active ? '#f44336' : '#37c13e',
                                }}
                            >
                                {fire.active ? 'Active' : 'Contained'}
                            </div>
                        </div>
                        {fire.updatedTimestamp && (
                            <div className="fire-popup-main-left-subheader-lastupdated">
                                <div className="fire-popup-main-left-subheader-lastupdated-title">Last Updated</div>
                                <LiveTime value={fire.updatedTimestamp}/>
                            </div>
                        )}
                    </div> 
                    <div className="fire-popup-main-left-date">
                        <img src={calendar} className="fire-popup-main-left-date-icon" alt="Calendar" />
                        <div className="fire-popup-main-left-date-value">
                            {fire.startTimestamp
                                ? formatDate(fire.startTimestamp)
                                : fire.starttimestamp
                                ? formatDate(fire.starttimestamp)
                                : 'Not Available'
                            }
                        </div>
                    </div>
                    <div className="fire-popup-main-left-location">
                        <img src={locationIcon} className="fire-popup-main-left-location-icon" alt="Location" />
                        <div className="fire-popup-main-left-location-value">
                            {`${fire.latitude.toFixed(5)}, ${fire.longitude.toFixed(5)}`}
                        </div>
                    </div>
                </div>
                <div className="fire-popup-main-right">
                    <PieChart 
                        percentage={fire.perContained}
                        size={90}
                        type='containment'
                    />
                    <div className="fire-popup-main-right-caption">Containment</div>
                </div>
            </div>
            {((fire.perContained || fire.perContained === 0) && fire.last_containment && (fire.last_containment.percontained || fire.last_containment.percontained === 0) && fire.last_containment.timestamp) && (fire.perContained - fire.last_containment.percontained !== 0) &&
                <div className='fire-card-main-left-containment-update'>
                <WarningCircle size={24} />
                <div className='fire-card-main-left-containment-update-text'>
                    {`Fire containment has `}
                    <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}>{fireContainmentChange(fire.last_containment.percontained, fire.perContained)}</span>
                    <span 
                        style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}
                    >
                        {` in the span of ${timeDifference(fire.last_containment.timestamp, fire.updatedTimestamp ? fire.updatedTimestamp : fire.startTimestamp)}.`}
                    </span>
                </div>
            </div>}
            <div style={{ paddingTop: '1.5rem' }}>
             {tabStageSwitch(tab)}    
            </div>
        </div>
        </div>
    )
}